import { useEffect, useState } from 'react';

import { CheckOutlined } from '@ant-design/icons';
import { Button, Card } from 'antd';
import { useTranslation } from 'react-i18next';

import { OperationSupervision } from 'models/OperationRequest';
import { Store } from 'models/Store';

import {
  SupervisionMap,
  autoSplit,
  buildSupervisionPayload,
  countFor,
  effectiveMap,
  missingStores,
} from '../data/supervision';
import SupervisorDistribution from './SupervisorDistribution';

import './SupervisorStep.scss';

interface SupervisorStepProps {
  stores: Store[];
  supervisors: string[];
  initial: OperationSupervision;
  saving?: boolean;
  onDone: (supervision: OperationSupervision) => void;
}

function SupervisorStep({
  stores,
  supervisors,
  initial,
  saving,
  onDone,
}: SupervisorStepProps) {
  const { t } = useTranslation();
  const i18nBase =
    'scenes.requirements_operation.components.assign_people.supervisor_step';

  const [sups, setSups] = useState<string[]>(initial.sups);
  const [map, setMap] = useState<SupervisionMap>(initial.map);

  useEffect(() => {
    setSups(initial.sups);
    setMap(initial.map);
  }, [initial]);

  const options = [
    ...supervisors,
    ...initial.sups.filter((s) => !supervisors.includes(s)),
  ];
  const eff = effectiveMap(stores, sups, map);
  const missing = missingStores(stores, sups, eff);
  const ok = sups.length > 0 && missing.length === 0;

  const toggle = (name: string) =>
    setSups((prev) =>
      prev.includes(name) ? prev.filter((x) => x !== name) : [...prev, name]
    );

  const footerMessage = () => {
    if (sups.length === 0) return t(`${i18nBase}.footer.pick_one`);
    if (sups.length === 1) {
      return t(`${i18nBase}.footer.single`, { name: sups[0] });
    }
    return ok
      ? t(`${i18nBase}.footer.all_assigned`)
      : t(`${i18nBase}.footer.assign_each`);
  };

  return (
    <Card bordered className="supervisor-step">
      <div className="supervisor-step__head">
        <div className="supervisor-step__eyebrow">
          {t(`${i18nBase}.eyebrow`)}
        </div>
        <div className="supervisor-step__title">{t(`${i18nBase}.title`)}</div>
        <p className="supervisor-step__description">
          {t(`${i18nBase}.description`)}
        </p>
      </div>

      <div className="supervisor-step__body">
        <div className="supervisor-step__label">
          {t(`${i18nBase}.supervisors_label`)}
        </div>
        {options.length === 0 ? (
          <div className="supervisor-step__empty">
            {t(`${i18nBase}.no_supervisors`)}
          </div>
        ) : (
          <div className="supervisor-step__chips">
            {options.map((name) => {
              const on = sups.includes(name);
              return (
                <button
                  key={name}
                  type="button"
                  role="checkbox"
                  aria-checked={on}
                  className={`supervisor-step__chip${
                    on ? ' supervisor-step__chip--on' : ''
                  }`}
                  onClick={() => toggle(name)}
                >
                  <span className="supervisor-step__chip-box">
                    {on && <CheckOutlined />}
                  </span>
                  <span>{name}</span>
                  {on && sups.length > 1 && (
                    <span className="supervisor-step__chip-count">
                      {t(`${i18nBase}.store_count`, {
                        count: countFor(stores, eff, name),
                      })}
                    </span>
                  )}
                </button>
              );
            })}
          </div>
        )}

        {sups.length > 1 && (
          <SupervisorDistribution
            stores={stores}
            sups={sups}
            eff={eff}
            missingCount={missing.length}
            onAuto={() => setMap(autoSplit(stores, sups))}
            onSet={(ids, sup) =>
              setMap((prev) => ({
                ...prev,
                ...Object.fromEntries(ids.map((id) => [id, sup])),
              }))
            }
          />
        )}
      </div>

      <div className="supervisor-step__footer">
        <span className="supervisor-step__footer-message">
          {footerMessage()}
        </span>
        <Button
          type="primary"
          className="supervisor-step__continue"
          disabled={!ok}
          loading={saving}
          onClick={() =>
            ok && onDone(buildSupervisionPayload(stores, sups, map))
          }
        >
          {t(`${i18nBase}.buttons.continue`)}
        </Button>
      </div>
    </Card>
  );
}

export default SupervisorStep;
